import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtDT = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Line({ label, value, tone = "text-slate-700" }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={cn("text-sm font-medium", tone)}>{value}</span>
    </div>
  );
}

export default function TaxProfileSummaryCard({ clientId, workspacePath }) {
  const [s, setS] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get(`/tax-profile/${clientId}/summary`).then((r) => setS(r.data)).catch(() => setS({}));
  }, [clientId]);
  if (!s) return <div className="flex justify-center py-8"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>;

  const riskTone = s.risk_level === "high" ? "text-rose-600" : s.risk_level === "moderate" ? "text-amber-600" : "text-emerald-600";
  const cxTone = ["complex", "highly_complex"].includes(s.estimated_complexity) ? "text-rose-600" : s.estimated_complexity === "moderate" ? "text-amber-600" : "text-emerald-600";

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tax-profile-summary-card">
      <div className="flex items-center gap-2 mb-4">
        <Icons.ClipboardList className="h-5 w-5 text-blue-500" />
        <h3 className="font-heading text-base font-semibold text-[#171B2E]">Tax Profile Summary</h3>
      </div>

      <div className="flex items-center gap-5 mb-4">
        <div className="text-center">
          <div className="text-2xl font-heading font-bold text-blue-600" data-testid="tps-completion">{s.completion ?? 0}%</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Complete</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-heading font-bold text-fuchsia-600" data-testid="tps-confidence">{s.confidence ?? 0}%</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Verified</div>
        </div>
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${s.completion ?? 0}%` }} />
        </div>
      </div>

      <div className="space-y-0">
        <Line label="Risk Level" value={cap(s.risk_level) || "—"} tone={riskTone} />
        <Line label="Complexity" value={cap(s.estimated_complexity) || "—"} tone={cxTone} />
        <Line label="Missing Prior Years" value={s.missing_prior_years ?? 0} tone={s.missing_prior_years ? "text-amber-600" : "text-slate-700"} />
        <Line label="FBAR Candidate" value={s.fbar_required_candidate ? "Yes" : "No"} tone={s.fbar_required_candidate ? "text-amber-600" : "text-slate-500"} />
        <Line label="Foreign Accounts" value={s.foreign_accounts ? "Yes" : "No"} tone={s.foreign_accounts ? "text-amber-600" : "text-slate-500"} />
        <Line label="Thai Tax Candidate" value={s.thai_tax_candidate ? "Yes" : "No"} tone={s.thai_tax_candidate ? "text-amber-600" : "text-slate-500"} />
        <Line label="Last Updated" value={fmtDT(s.updated_at)} tone="text-slate-500" />
      </div>

      <button onClick={() => { try { localStorage.setItem(`tf.ws.lastTab.${clientId}`, "tax"); } catch { /* ignore */ } nav(workspacePath || `/client-workspace/${clientId}`); }} data-testid="tps-open-full"
        className="mt-4 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
        <Icons.ArrowUpRight className="h-4 w-4" />Open Full Tax Profile
      </button>
    </div>
  );
}
